import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Tag,
  Calendar,
  Eye,
  Loader2,
  AlertCircle,
  ArrowLeft,
  Search,
  X,
  TrendingUp,
  Clock,
  User,
  BookOpen,
  ChevronRight,
  Sparkles,
  Flame,
  Grid3x3,
  List,
  Filter,
  ChevronDown,
  Hash,
  Heart,
  MessageCircle,
  Share2,
} from "lucide-react";
import { blogService } from "../../api/services/blogService";
import { format } from "date-fns";

// Blog Image Component for BLOB handling
const BlogImage = ({ blog, className }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    if (blog?.featuredImageBlob) {
      setImageUrl(blog.featuredImageBlob);
      setImageLoading(false);
    } else if (blog?.featuredImage) {
      setImageUrl(blog.featuredImage);
      setImageLoading(false);
    } else {
      setImageLoading(false);
    }
  }, [blog]);

  if (imageLoading) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <Loader2 size={24} className="animate-spin text-primary-500" />
      </div>
    );
  }

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={blog?.title}
        className={`object-cover ${className}`}
      />
    );
  }

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <BookOpen className="w-12 h-12 text-gray-400" />
    </div>
  );
};

// Popular Tags Cloud Component
const PopularTagsCloud = ({ tags, currentTag, onTagClick }) => {
  const getTagSize = (count, maxCount) => {
    const minSize = 0.875; // text-sm
    const maxSize = 1.5; // text-2xl
    const size = minSize + (count / maxCount) * (maxSize - minSize);
    return `${size}rem`;
  };

  const maxCount = Math.max(...tags.map((t) => t.count || 1), 1);

  return (
    <div className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl p-6 border border-white/30 dark:border-gray-800/30">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
          <Hash className="w-4 h-4 text-white" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Popular Tags
        </h3>
      </div>
      <div className="flex flex-wrap gap-3">
        {tags.slice(0, 20).map((tagItem) => (
          <button
            key={tagItem.name}
            onClick={() => onTagClick(tagItem.name)}
            className={`inline-block transition-all duration-300 hover:scale-105 ${
              currentTag === tagItem.name
                ? "text-primary-600 dark:text-primary-400 font-semibold"
                : "text-gray-600 dark:text-gray-400 hover:text-primary-500"
            }`}
            style={{ fontSize: getTagSize(tagItem.count || 1, maxCount) }}
          >
            #{tagItem.name}
            {tagItem.count && (
              <span className="text-xs text-gray-400 ml-1">
                ({tagItem.count})
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

// Tag Info Card Component
const TagInfoCard = ({ tag, postCount }) => {
  return (
    <div className="bg-gradient-to-br from-primary-500/10 to-secondary-500/10 backdrop-blur-xl rounded-2xl p-6 border border-primary-200/30 dark:border-primary-800/30 text-center">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 shadow-lg mb-4">
        <Hash className="w-10 h-10 text-white" />
      </div>
      <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-2">
        #{tag}
      </h1>
      <p className="text-gray-600 dark:text-gray-400 mb-4">
        {postCount} {postCount === 1 ? "article" : "articles"} tagged with "
        {tag}"
      </p>
      <div className="flex flex-wrap justify-center gap-3">
        <div className="flex items-center gap-2 px-4 py-2 bg-white/40 dark:bg-gray-900/40 rounded-full">
          <BookOpen className="w-4 h-4 text-primary-500" />
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {postCount} posts
          </span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-white/40 dark:bg-gray-900/40 rounded-full">
          <Eye className="w-4 h-4 text-primary-500" />
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Popular content
          </span>
        </div>
      </div>
    </div>
  );
};

// Blog Card Component
const BlogCard = ({ blog, index }) => {
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ y: -4 }}
      className="group bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/30 dark:border-gray-800/30 hover:shadow-xl transition-all duration-300"
    >
      <Link to={`/blog/${blog.slug}`} className="block">
        <div className="aspect-[16/11] overflow-hidden bg-gradient-to-br from-primary-500/10 to-secondary-500/10">
          <BlogImage
            blog={blog}
            className="w-full h-full group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-5">
          <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              <span>
                {format(
                  new Date(blog.publishedAt || blog.createdAt),
                  "MMM dd, yyyy",
                )}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{blog.readingTime || 5} min read</span>
            </div>
            <div className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              <span>{blog.views || 0}</span>
            </div>
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
            {blog.title}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2 mb-4">
            {blog.excerpt ||
              blog.content?.substring(0, 100)?.replace(/<[^>]*>/g, "")}
          </p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center">
                <User className="w-3 h-3 text-white" />
              </div>
              <span className="text-xs text-gray-500">
                {blog.author || "Anonymous"}
              </span>
            </div>
            <span className="text-primary-600 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
              Read More <ChevronRight className="w-3 h-3" />
            </span>
          </div>
        </div>
      </Link>
    </motion.article>
  );
};

// Blog List Item Component
const BlogListItem = ({ blog, index }) => {
  return (
    <motion.article
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      className="group bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/30 dark:border-gray-800/30 hover:shadow-xl transition-all duration-300"
    >
      <div className="flex flex-col md:flex-row">
        <Link to={`/blog/${blog.slug}`} className="md:w-72 overflow-hidden">
          <div className="aspect-[16/10] md:aspect-[4/3] overflow-hidden bg-gradient-to-br from-primary-500/10 to-secondary-500/10">
            <BlogImage
              blog={blog}
              className="w-full h-full group-hover:scale-105 transition-transform duration-500"
            />
          </div>
        </Link>
        <div className="flex-1 p-6">
          <div className="flex items-center gap-3 text-xs text-gray-500 mb-3 flex-wrap">
            <div className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              <span>
                {format(
                  new Date(blog.publishedAt || blog.createdAt),
                  "MMM dd, yyyy",
                )}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              <span>{blog.readingTime || 5} min read</span>
            </div>
            <div className="flex items-center gap-1">
              <Eye className="w-3.5 h-3.5" />
              <span>{blog.views || 0} views</span>
            </div>
          </div>
          <Link to={`/blog/${blog.slug}`}>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
              {blog.title}
            </h3>
          </Link>
          <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
            {blog.excerpt ||
              blog.content?.substring(0, 180)?.replace(/<[^>]*>/g, "")}
          </p>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {blog.author || "Anonymous"}
              </span>
            </div>
            <Link
              to={`/blog/${blog.slug}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500/10 hover:bg-primary-500 text-primary-600 hover:text-white rounded-xl transition-all duration-300 text-sm font-medium"
            >
              Read Article
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

const BlogTags = () => {
  const { tag } = useParams();
  const [blogs, setBlogs] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("latest");
  const [showFilters, setShowFilters] = useState(false);
  const blogsPerPage = 9;

  useEffect(() => {
    fetchTagBlogs();
    fetchAllTags();
  }, [tag, currentPage, sortBy]);

  const fetchTagBlogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await blogService.getBlogsByTag(tag, {
        page: currentPage,
        limit: blogsPerPage,
        sortBy:
          sortBy === "latest"
            ? "publishedAt"
            : sortBy === "popular"
              ? "views"
              : "publishedAt",
        sortOrder: "DESC",
      });
      const data = response?.data || response;
      let blogsData = data?.blogs || data?.items || [];

      if (sortBy === "trending") {
        blogsData = blogsData.sort((a, b) => (b.views || 0) - (a.views || 0));
      }

      setBlogs(blogsData);
      setTotalPages(data?.totalPages || data?.pagination?.totalPages || 1);
    } catch (err) {
      setError("No posts found with this tag");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTags = async () => {
    try {
      const response = await blogService.getPopularTags(30);
      const data = response?.data || response;
      setAllTags(data?.tags || []);
    } catch (err) {
      console.error("Error fetching tags:", err);
    }
  };

  const handleTagClick = (newTag) => {
    window.location.href = `/blog/tag/${encodeURIComponent(newTag)}`;
  };

  const filteredBlogs = blogs.filter(
    (blog) =>
      blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.content?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const sortOptions = [
    { value: "latest", label: "Latest", icon: Sparkles },
    { value: "popular", label: "Most Popular", icon: Flame },
    { value: "trending", label: "Trending", icon: TrendingUp },
  ];

  const handleSort = (value) => {
    setSortBy(value);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2
            size={48}
            className="animate-spin text-primary-500 mx-auto mb-4"
          />
          <p className="text-gray-600 dark:text-gray-400">
            Loading tag content...
          </p>
        </div>
      </div>
    );
  }

  if (error && blogs.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
              <Tag className="w-12 h-12 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Tag Not Found
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  const postCount = blogs.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 border border-gray-200 dark:border-gray-900 rounded-2xl shadow-lg">
      {/* Blur Background Layers */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[40%] -left-[20%] w-[80%] h-[80%] bg-gradient-to-br from-primary-200/20 to-secondary-200/20 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-[40%] -right-[20%] w-[80%] h-[80%] bg-gradient-to-tl from-primary-300/10 to-secondary-300/10 dark:from-primary-800/10 dark:to-secondary-800/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      {/* Hero Section with Tag Info */}
      <section className="relative pt-20 pb-12 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-full border border-white/30 dark:border-gray-800/30 shadow-sm mb-6">
                  <Tag className="w-4 h-4 text-primary-500" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Tag Archive
                  </span>
                </div>
              </div>
              <TagInfoCard tag={tag} postCount={postCount} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Search and Filter Bar */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl border border-white/30 dark:border-gray-800/30 p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search in this tag..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-4 top-1/2 -translate-y-1/2"
                  >
                    <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-3 bg-white/50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-white/70 transition-all"
              >
                <Filter className="w-4 h-4" />
                <span className="text-sm">Filters</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${showFilters ? "rotate-180" : ""}`}
                />
              </button>

              <div className="flex bg-white/50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-3 transition-all ${viewMode === "grid" ? "bg-primary-500 text-white" : "text-gray-600 dark:text-gray-400 hover:bg-white/70"}`}
                >
                  <Grid3x3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-3 transition-all ${viewMode === "list" ? "bg-primary-500 text-white" : "text-gray-600 dark:text-gray-400 hover:bg-white/70"}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Filter Panel */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700"
                >
                  <div className="flex flex-wrap gap-4 items-center">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Sort by:
                    </span>
                    <div className="flex gap-2">
                      {sortOptions.map((option) => {
                        const Icon = option.icon;
                        const isActive = sortBy === option.value;
                        return (
                          <button
                            key={option.value}
                            onClick={() => handleSort(option.value)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                              isActive
                                ? "bg-primary-500 text-white shadow-md"
                                : "bg-white/50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 hover:bg-white/70"
                            }`}
                          >
                            <Icon className="w-3.5 h-3.5" />
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto">
          {filteredBlogs.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <Tag className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                No posts found
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                {searchTerm
                  ? "No articles match your search criteria."
                  : "This tag has no articles yet."}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                >
                  Clear Search
                </button>
              )}
            </motion.div>
          ) : (
            <>
              <div className="mb-6 flex justify-between items-center">
                <p className="text-sm text-gray-500">
                  Showing {filteredBlogs.length} of {blogs.length} articles
                </p>
              </div>

              {viewMode === "grid" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredBlogs.map((blog, index) => (
                    <BlogCard key={blog.id} blog={blog} index={index} />
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredBlogs.map((blog, index) => (
                    <BlogListItem key={blog.id} blog={blog} index={index} />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && filteredBlogs.length > 0 && (
            <div className="flex justify-center gap-2 mt-12">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 rounded-lg bg-white/40 dark:bg-gray-900/40 backdrop-blur-sm border border-white/30 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/60 transition-all"
              >
                Previous
              </button>
              <div className="flex gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-10 h-10 rounded-lg transition-all ${
                        currentPage === pageNum
                          ? "bg-primary-500 text-white shadow-md"
                          : "bg-white/40 dark:bg-gray-900/40 backdrop-blur-sm border border-white/30 hover:bg-white/60"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="px-4 py-2 rounded-lg bg-white/40 dark:bg-gray-900/40 backdrop-blur-sm border border-white/30 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/60 transition-all"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Popular Tags Cloud Section */}
      {allTags.length > 0 && (
        <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-7xl mx-auto">
            <PopularTagsCloud
              tags={allTags}
              currentTag={tag}
              onTagClick={handleTagClick}
            />
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogTags;
